import { useEffect, useMemo, useState } from 'react';
import { getProductTierSlides } from '../assets/productTierImages';
import { supportsProductTiers } from '../data/productTiers';
import OptimizedImage from './OptimizedImage';

const SLIDE_INTERVAL = 5000;

function ProductTierShowcase({ product, className = '' }) {
  const slides = useMemo(() => getProductTierSlides(product.id), [product.id]);
  const [active, setActive] = useState(0);

  useEffect(() => {
    setActive(0);
  }, [product.id]);

  useEffect(() => {
    if (slides.length < 2) return undefined;

    const media = window.matchMedia
      ? window.matchMedia('(prefers-reduced-motion: reduce)')
      : null;
    if (media && media.matches) return undefined;

    const timer = setInterval(() => {
      setActive((current) => (current + 1) % slides.length);
    }, SLIDE_INTERVAL);

    return () => clearInterval(timer);
  }, [slides.length, product.id]);

  if (!supportsProductTiers(product) || slides.length === 0) {
    return (
      <div className={`product-tier-showcase product-tier-showcase-fallback ${className}`.trim()}>
        <OptimizedImage
          image={product.image}
          alt={product.name}
          className="product-tier-fallback-image"
        />
      </div>
    );
  }

  const activeSlide = slides[active];

  if (slides.length < 2) {
    return (
      <div className={`product-tier-showcase ${className}`.trim()}>
        <div className="product-tier-stage">
          <OptimizedImage
            image={slides[0].image}
            alt={product.name}
            className="product-tier-slide is-active"
            loading="eager"
          />
        </div>
        <p className="product-tier-label">{slides[0].label}</p>
      </div>
    );
  }

  return (
    <div className={`product-tier-showcase ${className}`.trim()}>
      <div
        className="product-tier-stage"
        aria-live="polite"
        aria-label={`Montage de ${product.name}`}
      >
        {slides.map((slide, index) => (
          <OptimizedImage
            key={`${product.id}-tier-${index}`}
            image={slide.image}
            alt=""
            className={`product-tier-slide ${index === active ? 'is-active' : ''}`}
            loading={index === 0 ? 'eager' : 'lazy'}
          />
        ))}
      </div>

      <p className="product-tier-label">{activeSlide.label}</p>
      <p className="product-tier-caption">
        Montage en paliers — {active + 1}/{slides.length}
      </p>

      <div className="product-tier-dots" aria-hidden="true">
        {slides.map((_, index) => (
          <span
            key={index}
            className={`product-tier-dot ${index === active ? 'is-active' : ''}`}
          />
        ))}
      </div>
    </div>
  );
}

export default ProductTierShowcase;
